import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Alert,
  Divider,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageContainer from 'src/components/container/PageContainer';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import config from 'src/config';
import i18n from 'src/i18n';

export interface Device {
  id: number;
  sku: number;
  manufacturerId?: string;
  skuDescFrench?: string;
  skuDescEnglish?: string;
  feeDeductible?: string;
  feeSheetDeductible?: string;
}
export interface Manufacturer {
  id: number;
  displayName: string;
}

const Search = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [deviceFound, setDeviceFound] = useState<boolean | null>(null);
  const [listManufacturer, setlistManufacturer] = useState<Manufacturer[]>([]);
  const [listDevices, setlistDevices] = useState<Device[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<Device[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState<boolean>(false);
  const [device, setDevice] = useState<Device | null>();
  const location = useLocation();

  const url = config.api.apiMms.URL;
  const { t } = useTranslation();
  const fetchManufacturerList = () => {
    axios
      .get(url + 'manufacturers')
      .then((response) => {
        const modifiedData: Manufacturer[] = response.data.map((item: any) => ({
          id: item.id,
          displayName: item.displayName,
        }));
        setlistManufacturer(modifiedData);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    let language = queryParams.get('language') || 'en';

    if (language === 'en' || language === 'fr') {
      language += '_CA';
    }

    if (language && ['en_CA', 'fr_CA'].includes(language)) {
      i18n.changeLanguage(language);
    }
  }, []);

  const fetchDevicesList = () => {
    setIsLoadingDevices(true);
    axios
      .get(url + 'getfiltereddevice')
      .then((response) => {
        const modifiedData: Device[] = response.data.map((item: any) => ({
          id: item.id,
          sku: item.sku,
          skuDescFrench: item.skuDescFrench,
          skuDescEnglish: item.skuDescEnglish,
        }));
        setlistDevices(modifiedData);
        setIsLoadingDevices(false);
      })
      .catch((error) => {
        setIsLoadingDevices(false);
      });
  };

  useEffect(() => {
    fetchDevicesList();
    fetchManufacturerList();
  }, []);

  const handleManufacturerChange = (manufacturerId: string) => {
    setSelectedManufacturer(manufacturerId);
    setSelectedModel('');
    fetchDevicesByManufacturer(manufacturerId);
  };

  const fetchDevicesByManufacturer = (manufacturerId: string) => {
    setDevice(null);
    axios
      .get(url + `/getDevicesByManufacturer?manufacture_id=${manufacturerId}`)
      .then((response) => {
        const modifiedData: Device[] = response.data.map((item: any) => ({
          id: item.id,
          sku: item.sku,
          skuDescFrench: item.skuDescFrench,
          skuDescEnglish: item.skuDescEnglish,
        }));
        setFilteredDevices(modifiedData);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleModelChange = (sku: string) => {
    setSelectedModel(sku);
    fetchDevicePrice(sku);
  };

  const fetchDevicePrice = (sku: string) => {
    axios
      .get(url + `/getDevicePrice?sku=${sku}`)
      .then((response) => {
        if (
          response.data[0].feeSheetDeductible ||
          response.data[0].feeDeductible
        ) {
          setDevice(response.data[0]);
          setDeviceFound(true);
        } else {
          setDeviceFound(false);
        }
      })
      .catch((error) => {
        console.log(error);
        setDeviceFound(false);
      });
  };

  return (
    <PageContainer
      title={t('views.search.ReplacementService')}
      description={t('views.search.SelectYourDeviceForReplacementService')}
    >
      {isLoadingDevices ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress size={50} />
        </Box>
      ) : (
        <Box sx={{ position: 'relative', '&:before': {} }}>
          <Grid
            container
            spacing={0}
            justifyContent="center"
            sx={{ height: '100vh' }}
          >
            <Grid
              item
              xs={12}
              sm={8}
              md={6}
              lg={4}
              xl={3}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Card
                elevation={9}
                sx={{ p: 4, zIndex: 1, width: '100%', maxWidth: '500px' }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={2}
                >
                  <Typography variant="h6" gutterBottom>
                    {t('views.search.intro')}
                  </Typography>
                </Box>

                <Box
                  component="form"
                  noValidate
                  autoComplete="off"
                  sx={{ mt: 2 }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    style={{ fontWeight: 600 }}
                  >
                    {t('views.search.SearchYourDevice')}
                  </Typography>
                  <Autocomplete
                    fullWidth
                    disablePortal
                    options={listDevices}
                    getOptionLabel={(option) =>
                      i18n.language === 'fr_CA'
                        ? `${option.skuDescFrench}`
                        : `${option.skuDescEnglish}`
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {i18n.language === 'fr_CA'
                          ? option.skuDescFrench
                          : option.skuDescEnglish}
                      </li>
                    )}
                    onChange={(event, value) => {
                      if (value) {
                        const sku = value.sku as unknown as string;
                        setSelectedModel(sku);
                        fetchDevicePrice(sku);
                      } else {
                        setSelectedModel('');
                      }
                    }}
                    onInputChange={(event, value) => setSearchQuery(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        InputProps={{ ...params.InputProps }}
                      />
                    )}
                  />

                  <Divider sx={{ my: 2 }} style={{ fontWeight: 600 }}>
                    Or
                  </Divider>
                  <Typography
                    variant="h6"
                    gutterBottom
                    style={{ fontWeight: 600 }}
                  >
                    {t('views.search.ChooseManufacturer')}
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    value={selectedManufacturer}
                    onChange={(e) => handleManufacturerChange(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    {listManufacturer.map((manufacturer) => (
                      <MenuItem key={manufacturer.id} value={manufacturer.id}>
                        {manufacturer.displayName}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography
                    variant="h6"
                    gutterBottom
                    style={{ fontWeight: 600 }}
                  >
                    {t('views.search.ChooseModel')}
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    value={selectedModel}
                    onChange={(e) => handleModelChange(e.target.value)}
                    disabled={!selectedManufacturer || isLoadingDevices}
                    sx={{ mb: 3 }}
                  >
                    {isLoadingDevices ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        p={2}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      filteredDevices.map((device) => (
                        <MenuItem key={device.sku} value={device.sku}>
                          {i18n.language === 'fr_CA'
                            ? device.skuDescFrench
                            : device.skuDescEnglish}
                        </MenuItem>
                      ))
                    )}
                  </TextField>

                  {deviceFound === false && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {t(
                        'views.search.SorryYourDeviceIsNotEligibleForTheSmartCarePlan'
                      )}
                    </Alert>
                  )}

                  {deviceFound && device && (
                    <Box
                      sx={{
                        mt: 2,
                        padding: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        boxShadow: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 'bold', mb: 1 }}
                      >
                        {i18n.language === 'fr_CA'
                          ? device?.skuDescFrench
                          : device?.skuDescEnglish}
                      </Typography>

                      {device && (
                        <Box sx={{ mb: 2 }}>
                          {device.feeSheetDeductible ? (
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 'bold', display: 'inline' }}
                            >
                              ${device.feeSheetDeductible}
                            </Typography>
                          ) : (
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 'bold', display: 'inline' }}
                            >
                              ${device.feeDeductible}
                            </Typography>
                          )}
                          <Typography
                            color="text.secondary"
                            sx={{ ml: 1, display: 'inline' }}
                          >
                            {t('views.search.ExcludingTaxes')}
                          </Typography>
                        </Box>
                      )}

                      <Typography
                        variant="body2"
                        sx={{ color: 'text.primary', mb: 1 }}
                      >
                        {t('views.search.EffectiveFromToday')}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: 'text.primary' }}
                      >
                        {t(
                          'views.search.ToRequestAdeviceReplacementContactAPprogramAdvisorAt'
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
    </PageContainer>
  );
};

export default Search;
